/**
 * Feature Flags Admin API Client Service
 *
 * Wraps the Fastify API at `/api/v1/feature-flags` for admin-plane CRUD over
 * the flag catalog, per-tenant overrides, percentage rollouts, scheduled
 * flips, and the flag audit log. Plus the per-caller evaluation endpoint
 * `GET /api/v1/feature-flags/evaluate/:key` used by ad-hoc UI gates that
 * don't go through the analytics provider.
 *
 * Phase 2 Wave A. Mirrors the `booksApi` style: each function returns the
 * envelope's `data` payload and throws on `success: false`.
 *
 * WIRE CONTRACT: the route (`apps/api/components/routes/feature-flags.ts`) is
 * the contract, and its request bodies are snake_case under
 * `additionalProperties: false` + a `.strict()` Zod parse. Fastify's AJV runs
 * with `removeAdditional: true`, so a camelCase key is DELETED before the
 * handler sees it: a required one turns into a 400 (every create and every
 * schedule failed this way), an optional one is dropped silently with a 200
 * (override expiry, rollout salt / enabled value). The inputs below stay
 * camelCase for callers; each write maps them to the declared names, pinned by
 * `packages/client/test/featureFlagsApi.test.ts`.
 *
 * NOTE: hot-path evaluation in apps lives in `@order-up/analytics`'s
 * `IFeatureFlags` provider — this file is admin/management surface only.
 */
import type {
    FlagScopeType,
    FlagValue,
    FlagValueType,
    IFlag,
    IFlagAudit,
} from '@order-up/models';
import { createApiClient } from './apiClient';

const apiClient = createApiClient('/api/v1/feature-flags');

// ============================================================================
// Response envelope (CC-1)
// ============================================================================

interface ApiErrorPayload {
    code?: string;
    message?: string;
}

interface ApiEnvelope<T> {
    success: boolean;
    data: T;
    error?: ApiErrorPayload;
}

const unwrap = async <T,>(promise: Promise<ApiEnvelope<T>>): Promise<T> => {
    const res = await promise;
    if (!res || res.success === false) {
        const code = res?.error?.code;
        const message = res?.error?.message ?? 'Feature flags API error';
        throw new Error(code ? `${code}: ${message}` : message);
    }
    return res.data;
};

const isoOrUndefined = (d?: Date | null): string | undefined =>
    d ? d.toISOString() : undefined;

// ============================================================================
// Filters & inputs
// ============================================================================

export interface ListFeatureFlagsParams {
    archived?: boolean;
}

export interface CreateFeatureFlagInput {
    flagKey: string;
    description: string;
    valueType: FlagValueType;
    defaultValue: FlagValue;
    ownerEmail?: string | null;
    tags?: readonly string[];
    metadata?: Record<string, unknown>;
}

export interface UpdateFeatureFlagInput {
    description?: string;
    ownerEmail?: string | null;
    tags?: readonly string[];
    metadata?: Record<string, unknown>;
}

export interface SetDefaultValueInput {
    value: FlagValue;
    reason?: string;
}

export interface SetTenantOverrideInput {
    value: FlagValue;
    reason?: string;
    expiresAt?: Date | null;
}

export interface SetRolloutInput {
    percentage: number;
    hashSalt?: string | null;
    enabledValue?: FlagValue;
    reason?: string;
}

export interface ScheduleFlipInput {
    scopeType: FlagScopeType;
    scopeId?: string | null;
    targetValue: FlagValue;
    fireAt: Date;
}

/**
 * Query for `GET /:key/audit`. The route's strict query schema declares
 * exactly `scopeType`, `scopeId` and `limit` — an undeclared key (this used to
 * carry a `since` the route never accepted) is a 400, not an ignored filter.
 */
export interface ListFeatureFlagAuditParams {
    scopeType?: FlagScopeType;
    scopeId?: string;
    limit?: number;
}

export interface ArchiveFlagOptions {
    reason?: string;
}

export interface CancelScheduleOptions {
    reason?: string;
}

/**
 * Options for `evaluateFlag`. `tenantGuid` pins the evaluation to a specific
 * tenant instead of the caller's own — the route honors `?tenantGuid` for
 * surfaces that act on behalf of a selected business (admin/HQ consoles).
 */
export interface EvaluateFlagOptions {
    tenantGuid?: string;
}

/**
 * Evaluation result for `GET /api/v1/feature-flags/evaluate/:key`. The server
 * resolves precedence (override > rollout > default) for the caller's tenant
 * and answers `{ value }` ONLY — its response schema declares nothing else, so
 * `flagKey` is the key this call asked for and `source` is `'unknown'` until
 * the route reports one. Both are filled here because the persisted-seed
 * validator (`flagEvaluationStorage.ts`) requires them; left to the wire they
 * were always absent and every seed was discarded as malformed.
 */
export interface FlagEvaluationResult {
    flagKey: string;
    value: FlagValue;
    source: 'override' | 'rollout' | 'default' | 'archived' | 'unknown';
}

// ============================================================================
// Write acknowledgements — exactly what each route's 200/201 schema declares.
// The writes do NOT echo the row; callers that need it re-read (the hooks
// invalidate by the flag key they sent, never by a key read off these).
// ============================================================================

/** `POST /:key/archive` and `POST /:key/unarchive`. */
export interface ArchiveFlagResult {
    archived: boolean;
}

/** `PUT /:key/default`. */
export interface SetDefaultValueResult {
    flagKey: string;
}

/** `PUT /:key/overrides/:tenantGuid`. */
export interface SetTenantOverrideResult {
    flagKey: string;
    tenantGuid: string;
}

/** `PUT /:key/rollout`. */
export interface SetRolloutResult {
    flagKey: string;
    percentage: number;
}

/** `POST /:key/schedules` (201). */
export interface ScheduleFlipResult {
    id: string;
}

/** `DELETE /schedules/:id`. */
export interface CancelScheduleResult {
    id: string;
}

// ============================================================================
// Service
// ============================================================================

export const featureFlagsApi = {
    // ─── Catalog (flags) ────────────────────────────────────────────────
    listFeatureFlags: (
        params: ListFeatureFlagsParams = {},
    ): Promise<IFlag[]> =>
        unwrap(
            apiClient.get('/', {
                params: { archived: params.archived },
            }) as unknown as Promise<ApiEnvelope<IFlag[]>>,
        ),

    getFeatureFlag: (flagKey: string): Promise<IFlag> =>
        unwrap(
            apiClient.get(`/${flagKey}`) as unknown as Promise<
                ApiEnvelope<IFlag>
            >,
        ),

    createFeatureFlag: (input: CreateFeatureFlagInput): Promise<IFlag> =>
        unwrap(
            apiClient.post('/', {
                flag_key: input.flagKey,
                description: input.description,
                value_type: input.valueType,
                default_value: input.defaultValue,
                owner_email: input.ownerEmail,
                tags: input.tags,
                metadata: input.metadata,
            }) as unknown as Promise<ApiEnvelope<IFlag>>,
        ),

    updateFeatureFlag: (
        flagKey: string,
        patch: UpdateFeatureFlagInput,
    ): Promise<IFlag> =>
        unwrap(
            apiClient.patch(`/${flagKey}`, {
                description: patch.description,
                owner_email: patch.ownerEmail,
                tags: patch.tags,
                metadata: patch.metadata,
            }) as unknown as Promise<ApiEnvelope<IFlag>>,
        ),

    archiveFeatureFlag: (
        flagKey: string,
        opts: ArchiveFlagOptions = {},
    ): Promise<ArchiveFlagResult> =>
        unwrap(
            apiClient.post(
                `/${flagKey}/archive`,
                undefined,
                {
                    params: { reason: opts.reason },
                },
            ) as unknown as Promise<ApiEnvelope<ArchiveFlagResult>>,
        ),

    unarchiveFeatureFlag: (flagKey: string): Promise<ArchiveFlagResult> =>
        unwrap(
            apiClient.post(`/${flagKey}/unarchive`) as unknown as Promise<
                ApiEnvelope<ArchiveFlagResult>
            >,
        ),

    setDefaultValue: (
        flagKey: string,
        input: SetDefaultValueInput,
    ): Promise<SetDefaultValueResult> =>
        unwrap(
            apiClient.put(`/${flagKey}/default`, {
                value: input.value,
                reason: input.reason,
            }) as unknown as Promise<ApiEnvelope<SetDefaultValueResult>>,
        ),

    // ─── Per-tenant overrides ───────────────────────────────────────────
    setTenantOverride: (
        flagKey: string,
        tenantGuid: string,
        input: SetTenantOverrideInput,
    ): Promise<SetTenantOverrideResult> =>
        unwrap(
            apiClient.put(`/${flagKey}/overrides/${tenantGuid}`, {
                value: input.value,
                reason: input.reason,
                expires_at: isoOrUndefined(input.expiresAt),
            }) as unknown as Promise<ApiEnvelope<SetTenantOverrideResult>>,
        ),

    clearTenantOverride: async (
        flagKey: string,
        tenantGuid: string,
    ): Promise<void> => {
        await unwrap(
            apiClient.delete(
                `/${flagKey}/overrides/${tenantGuid}`,
            ) as unknown as Promise<ApiEnvelope<unknown>>,
        );
    },

    // ─── Percentage rollout ─────────────────────────────────────────────
    setRollout: (
        flagKey: string,
        input: SetRolloutInput,
    ): Promise<SetRolloutResult> =>
        unwrap(
            apiClient.put(`/${flagKey}/rollout`, {
                percentage: input.percentage,
                hash_salt: input.hashSalt,
                enabled_value: input.enabledValue,
                reason: input.reason,
            }) as unknown as Promise<ApiEnvelope<SetRolloutResult>>,
        ),

    clearRollout: async (flagKey: string): Promise<void> => {
        await unwrap(
            apiClient.delete(`/${flagKey}/rollout`) as unknown as Promise<
                ApiEnvelope<unknown>
            >,
        );
    },

    // ─── Audit log ──────────────────────────────────────────────────────
    listFeatureFlagAudit: (
        flagKey: string,
        params: ListFeatureFlagAuditParams = {},
    ): Promise<IFlagAudit[]> =>
        unwrap(
            apiClient.get(`/${flagKey}/audit`, {
                params: {
                    scopeType: params.scopeType,
                    scopeId: params.scopeId,
                    limit: params.limit,
                },
            }) as unknown as Promise<ApiEnvelope<IFlagAudit[]>>,
        ),

    // ─── Scheduled flips ────────────────────────────────────────────────
    scheduleFlip: (
        flagKey: string,
        input: ScheduleFlipInput,
    ): Promise<ScheduleFlipResult> =>
        unwrap(
            apiClient.post(`/${flagKey}/schedules`, {
                scope_type: input.scopeType,
                scope_id: input.scopeId,
                target_value: input.targetValue,
                fire_at: input.fireAt.toISOString(),
            }) as unknown as Promise<ApiEnvelope<ScheduleFlipResult>>,
        ),

    cancelSchedule: async (
        scheduleId: string,
        opts: CancelScheduleOptions = {},
    ): Promise<CancelScheduleResult> =>
        unwrap(
            apiClient.delete(`/schedules/${scheduleId}`, {
                params: { reason: opts.reason },
            }) as unknown as Promise<ApiEnvelope<CancelScheduleResult>>,
        ),

    // ─── Evaluation (any authenticated caller) ──────────────────────────
    evaluateFlag: async (
        flagKey: string,
        opts: EvaluateFlagOptions = {},
    ): Promise<FlagEvaluationResult> => {
        const data = await unwrap(
            apiClient.get(`/evaluate/${flagKey}`, {
                params: { tenantGuid: opts.tenantGuid },
            }) as unknown as Promise<ApiEnvelope<{ value?: FlagValue | null }>>,
        );
        return {
            flagKey,
            value: (data?.value ?? null) as FlagValue,
            source: 'unknown',
        };
    },
} as const;

// ============================================================================
// Convenience aliases — flat function-named surface mirroring `opsEmployees`.
// ============================================================================

export const listFeatureFlags = featureFlagsApi.listFeatureFlags;
export const getFeatureFlag = featureFlagsApi.getFeatureFlag;
export const createFeatureFlag = featureFlagsApi.createFeatureFlag;
export const updateFeatureFlag = featureFlagsApi.updateFeatureFlag;
export const archiveFeatureFlag = featureFlagsApi.archiveFeatureFlag;
export const unarchiveFeatureFlag = featureFlagsApi.unarchiveFeatureFlag;
export const setDefaultValue = featureFlagsApi.setDefaultValue;
export const setTenantOverride = featureFlagsApi.setTenantOverride;
export const clearTenantOverride = featureFlagsApi.clearTenantOverride;
export const setRollout = featureFlagsApi.setRollout;
export const clearRollout = featureFlagsApi.clearRollout;
export const listFeatureFlagAudit = featureFlagsApi.listFeatureFlagAudit;
export const scheduleFlip = featureFlagsApi.scheduleFlip;
export const cancelSchedule = featureFlagsApi.cancelSchedule;
export const evaluateFlag = featureFlagsApi.evaluateFlag;
